import { queryKeys } from '@/lib/query-keys';
import { createLogger } from '@/lib/logger';
import type {
} from '@octave/contracts';
import {
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  lineClient,
  } from '@/lib/api/ts-rest';

const log = createLogger('useLines');

export const useReassignMobileLine = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { lineId: string; identityId: string }>({
    mutationFn: async ({ lineId, identityId }) => {
      const res = await lineClient.reassignLine({
        params: { lineId },
        body: { identityId },
      });
      if (res.status !== 201) throw new Error('Failed to reassign');
      return true;
    },
    onSettled: (_, __, { lineId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.root() });
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.detail(lineId) });
    },
    onError: (error) => {
      log.error('Error reassigning mobile line', error);
      toast.error('Oups, une erreur est survenue lors de la réassignation');
    },
  });
};

export const useSuspendMobileLine = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { lineId: string }>({
    mutationFn: async ({ lineId }) => {
      const res = await lineClient.suspendLine({ params: { lineId } });
      if (res.status !== 201) throw new Error('Failed to suspend');
      return true;
    },
    onSettled: (_, __, { lineId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.root() });
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.detail(lineId) });
    },
  });
};

export const useUnsuspendMobileLine = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { lineId: string }>({
    mutationFn: async ({ lineId }) => {
      const res = await lineClient.unsuspendLine({ params: { lineId } });
      if (res.status !== 201) throw new Error('Failed to unsuspend');
      return true;
    },
    onSettled: (_, __, { lineId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.root() });
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.detail(lineId) });
    },
  });
};

export const useTerminateMobileLine = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { lineId: string }>({
    mutationFn: async ({ lineId }) => {
      const res = await lineClient.terminateLine({ params: { lineId } });
      if (res.status !== 201) throw new Error('Failed to terminate');
      return true;
    },
    onSettled: (_, __, { lineId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.root() });
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.detail(lineId) });
    },
  });
};

export const useUpgradeMobileLinePhonePlan = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { lineId: string; phonePlanId: string }>({
    mutationFn: async ({ lineId, phonePlanId }) => {
      const res = await lineClient.upgradePhonePlan({
        params: { lineId },
        body: { phonePlanId },
      });
      if (res.status !== 201) throw new Error('Failed to upgrade plan');
      return true;
    },
    onSettled: (_, __, { lineId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.root() });
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.detail(lineId) });
      window.location.reload();
    },
  });
};

export const useUpdatePackageOptionsMobileLine = () => {
  const queryClient = useQueryClient();

  return useMutation<
    boolean,
    Error,
    { lineId: string; packageOptions: { label: string; isEnabled: boolean }[] }
  >({
    mutationFn: async ({ lineId, packageOptions }) => {
      const res = await lineClient.updatePackageOptions({
        params: { lineId },
        body: { packageOptions },
      });
      if (res.status !== 201) throw new Error('Failed to update options');
      return true;
    },
    onSettled: (_, __, { lineId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.root() });
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.detail(lineId) });
    },
    onError: (error) => {
      log.error('Error updating package options', error);
      toast.error(
        'Oups, une erreur est survenue lors de la mise à jour des options du package'
      );
    },
  });
};

export const useCancelScheduledPhonePlans = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { lineId: string }>({
    mutationFn: async ({ lineId }) => {
      const res = await lineClient.cancelScheduledPhonePlans({
        params: { lineId },
      });
      if (res.status !== 201) throw new Error('Failed to cancel');
      return true;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.root() });
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.detail() });
    },
  });
};

export const useActivateInternationalOption = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { lineId: string }>({
    mutationFn: async ({ lineId }) => {
      const res = await lineClient.activateInternationalOption({
        params: { lineId },
      });
      if (res.status !== 201) throw new Error('Failed to activate option');
      return true;
    },
    onSettled: (_, __, { lineId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.root() });
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.detail(lineId) });
    },
  });
};

export const useDeactivateInternationalOption = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { lineId: string }>({
    mutationFn: async ({ lineId }) => {
      const res = await lineClient.deactivateInternationalOption({
        params: { lineId },
      });
      if (res.status !== 201) throw new Error('Failed to deactivate option');
      return true;
    },
    onSettled: (_, __, { lineId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.root() });
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.detail(lineId) });
    },
  });
};

export const useReactivateMobileLine = () => {
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { lineId: string }>({
    mutationFn: async ({ lineId }) => {
      const res = await lineClient.reactivateLine({ params: { lineId } });
      if (res.status !== 201) throw new Error('Failed to reactivate');
      return true;
    },
    onSettled: (_, __, { lineId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.root() });
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.detail(lineId) });
    },
  });
};

export const useSwapSimCard = () => {
  const queryClient = useQueryClient();

  return useMutation<
    boolean,
    Error,
    { lineId: string; simCardType: 'basic' | 'esim'; simCardId?: string }
  >({
    mutationFn: async ({ lineId, simCardType, simCardId }) => {
      const res = await lineClient.swapSimCard({
        params: { lineId },
        body: { simCardType, simCardId },
      });
      if (res.status !== 201) throw new Error('Failed to swap SIM');
      return true;
    },
    onSettled: (_, __, { lineId }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.root() });
      queryClient.invalidateQueries({ queryKey: queryKeys.lines.detail(lineId) });
    },
  });
};

export const useSendEsimInstructions = () => {
  return useMutation<
    { success: boolean },
    Error,
    { lineId: string; recipientEmail: string }
  >({
    mutationFn: async ({ lineId, recipientEmail }) => {
      const res = await lineClient.sendEsimInstructions({
        params: { lineId },
        body: { recipientEmail },
      });
      if (res.status !== 201) throw new Error('Failed to send instructions');
      return res.body;
    },
    onSuccess: () => {
      toast.success('Instructions eSIM envoyées par email avec succès');
    },
    onError: (error) => {
      toast.error(
        error.message || "Erreur lors de l'envoi des instructions eSIM"
      );
    },
  });
};
